<template>  
  <div id="SessionViewerTimeline">
    <template v-if="sessionFlow && !loading">
      <Card class="title-card">
        <CardSection>
          <ButtonGroup v-if="session.isActive" class="active-session-buttons">
            <Button type="destructive" @click="$emit('expire-session')">
              <div class="title-button-label-long">{{$t('SessionViewer.Expire Session')}}</div>
              <div class="title-button-label-short">{{$t('SessionViewer.Expire')}}</div>
            </Button>
            <Button @click="$emit('view-another-session')">
              <div class="title-button-label-long">{{$t('SessionViewer.View another session')}}</div>
              <div class="title-button-label-short">{{$t('SessionViewer.Another')}}</div>
            </Button>
            <ButtonGroupItem style="margin-left: auto;">
              <Button @click="$emit('refresh-session')">
                <div class="title-button-label-long"><Icon style="margin-right: 5px;" icon="fas fa-sync-alt"/>{{$t('SessionViewer.Refresh')}}</div>
                <div class="title-button-label-short"><Icon icon="fas fa-sync-alt"/></div>
              </Button>
            </ButtonGroupItem>
          </ButtonGroup>
          <ButtonGroup class="session-buttons">
            <Button v-if="hasPreviousSession" @click="goToSession(currentSessionIndex - 1)"><Icon class="arrow-left" icon="fas fa-arrow-left"/><div class="navigate-session-text">{{$t('SessionViewer.previousSession')}}</div></Button>
            <Button @click="viewSessionDetails = true">
              <div class="title-button-label-long">{{$t('SessionViewer.viewSessionDetails')}}</div>
              <div class="title-button-label-short">{{$t('Details')}}</div>
            </Button>
            <Button @click="$emit('show-user-timeline', false)">
              <div class="title-button-label-long">{{$t('SessionViewer.viewUserSessionHistory')}}</div>
              <div class="title-button-label-short">{{$t('History')}}</div>
            </Button>
            <ButtonGroupItem style="margin-left: auto">
              <Button class="reverse-order-button" :style="hasNextSession ? 'margin-right: 10px;' : ''" @click="$emit('reverse-timeline')"><i class="fas fa-sort"></i></Button>
              <Button v-if="hasNextSession" @click="goToSession(currentSessionIndex + 1)"><div class="navigate-session-text">{{$t('SessionViewer.nextSession')}}</div><Icon class="arrow-right" icon="fas fa-arrow-right"/></Button>
            </ButtonGroupItem>
          </ButtonGroup>
          <Filters
            class="filter"
            :filters="[
              {
                key: 'pageType',
                label: $t('SessionViewer.pageTypes'),
                type: 'select',
                placeholder: $t('SessionViewer.chooseAPageType'),
                multiple: true,
                options: pageTypeLabelValues
              },
              {
                key: 'hasEvent',
                label: $t('SessionViewer.Events'),
                type: 'select',
                placeholder: $t('SessionViewer.chooseAPageEvent'),
                multiple: true,
                options: pageEventLabelValues
              }
            ]"
            :applied-filters="filters"
            @update="val => filters = val"
            :query.sync="query"
          />
        </CardSection>
      </Card>
    </template>
    <LoadingLayout v-else/>
    <div v-if="shortenedMiniTimeline.length && !loading" class="mini-user-timeline">
      <div class="history-element"><div class="history-element-year">{{$t('now').toUpperCase()}}</div></div>
      <div 
        class="history-element" 
        :class="{ 'current-session': session && hist.sk === session.sessionKey && !loading, 'session': hist.type === 'session' }" 
        v-for="(hist, ind) in shortenedMiniTimeline" 
        :key="ind"
        @click="hist.type === 'session' ? goToSessionByKey(hist.sk) : null"
      >
        <div v-if="hist.type === 'year'" class="history-element-year">{{hist.year}}</div>
        <div v-else class="history-element-date">
          <div class="history-element-date-day">{{hist.day}}</div>
          <div class="history-element-date-month">{{hist.month}}</div>
        </div>
      </div>
      <div v-if="allowShowAll" @click="showAllMiniTimeline = true" class="mini-timeline-show-all">
        {{$t('SessionViewer.showAll')}}
      </div>
    </div>
    <div v-if="sessionFlow && flow && flow.length && !loading" class="timeline-wrapper">
      <div v-if="isIgnoring" class="ignore-warning-wrapper">
        <div class="ignore-warning"><Icon icon="fa fa-exclamation-circle"/>
          <div>{{ $t('SessionViewer.ignoreUserMessage', { value: formatDate(convertHxDate(session.userSite.ignoreUserUntil)) }) }}</div>
        </div>
      </div>
      <div class="timeline-date">
        <Formatter type="dateTimeLong" :value="reverse ? lastPage.timestamp : firstPage.timestamp"/>
      </div>
      <div class="timeline">
        <div v-for="(item, index) in flow" :key="index" class="timeline-item">
          <div v-if="extraInfo && extraInfo.pageIndex === item.page.index" :class="`${index % 2 === 0 ? `extra-info-card-right` : `extra-info-card-left`}`">
            <template v-if="extraInfo.type === 'segment'">
              <div class="sub-page-title">{{$t('SessionViewer.segmentDetails')}}</div>
              <hr class="hr-line"/>
              <div class="basic-info">
                <Stats>
                  <Stat :label="$t('ID')" :value="extraInfo.value.id"/>
                  <Stat :label="$t('Name')">{{extraInfo.value.name || '—'}}</Stat>
                  <Stat :label="$t('Type')" :value="segmentTypeMap[extraInfo.value.type]"/>
                  <Stat :label="$t('Status')" :value="formatStatus(extraInfo.value.status)"/>
                </Stats>
              </div>
            </template>
            <template v-else-if="extraInfo.type === 'widget'">
              <div class="sub-page-title">{{$t('SessionViewer.widgetDetails')}}</div>
              <hr class="hr-line"/>
              <div class="basic-info">
                <Stats>
                  <Stat :label="$t('ID')" :value="extraInfo.value.id"/>
                  <Stat :label="$t('Name')">{{extraInfo.value.name || '—'}}</Stat>
                  <Stat :label="$t('Type')" :value="extraInfo.value.type"/>
                  <Stat :label="$t('Status')" :value="formatStatus(extraInfo.value.status)"/>
                </Stats>
              </div>
            </template>
            <template v-else-if="extraInfo.type === 'offer'">
              <div class="sub-page-title">{{$t('SessionViewer.offerDetails')}}</div>
              <hr class="hr-line"/>
              <div class="basic-info">
                <Stats>
                  <Stat :label="$t('SessionViewer.promoCode')" :value="extraInfo.value.code"/>
                  <Stat :label="$t('Display')" :value="extraInfo.value.displayText"/>
                  <Stat :label="$t('Variant')" :value="extraInfo.value.settings && extraInfo.value.settings.classes"/>
                  <Stat :label="$t('Status')" :value="formatStatus(extraInfo.value.status)"/>
                  <Stat :label="$t('SessionViewer.minimumPurchase')">
                    {{getMinPurchase(extraInfo.value)}}
                  </Stat>
                  <Stat :label="$t('SessionViewer.codesUsed')" :value="extraInfo.value.numRewardsUsed"/>
                  <Stat :label="$t('Start Date')" type="dateTimeShort" :value="extraInfo.value.validFromDate"/>
                  <Stat :label="$t('End Date')" type="dateTimeShort" :value="extraInfo.value.expiryDate"/>
                </Stats>
                <!-- <pre>{{extraInfo.value}}</pre> -->
              </div>
            </template>
          </div>
          <div :class="`page-container ${index % 2 === 0 ? `page-left` : `page-right`}`"> 
            <div class="page-card" @click.prevent="page = item.page">
              <div>
                <div class="page-time">
                  <div>{{ humanizeDuration(item.page.timestamp, firstPage.timestamp) }}</div>
                </div>
                
                <div class="page-type">
                  <Icon class="page-type-icon" :icon="pageTypeIconMap[item.page.pageType]"/>
                  {{formatPageType(item.page.pageType)}}
                </div>
                <div class="page-index">
                  #{{item.page.index + 1}}
                </div>
              </div>

              <div v-if="item.page.pageType === 'prod'">
                <hr class="hr-line">
                <div class="product-info">
                  <div v-if="item.page.productImage" class="product-info-image">
                    <img :src="item.page.productImage" width="50"/>
                  </div>
                  <div class="product-info-name-value">
                    <div class="product-info-name" :class="{ 'with-img': item.page.productImage }">
                      {{item.page.productName || $t('SessionViewer.Product')}}
                      <div v-if="item.page.categoryName" class="product-category-name">{{item.page.categoryName}}</div>
                    </div>
                    <Formatter v-if="item.page.productPrice" class="product-price" :class="{ 'with-cat': item.page.categoryName }" type="currency" :currency="item.page.currencyIso || currency" :value="item.page.productPrice"/>
                  </div>
                </div>
              </div>
              <div v-else-if="item.page.pageType === 'cat'">
                <hr class="hr-line">
                <div class="basic-info">
                  <Stat :label="$t('SessionViewer.Category')">{{item.page.categoryName || $t('SessionViewer.noCategoryName')}}</Stat>
                </div>
              </div>
              <div v-else-if="item.page.pageType === 'page' || item.page.pageType === 'home' || item.page.pageType === 'srch' || item.page.pageType === 'blog' || !item.page.pageType">
                <hr class="hr-line">
                <div class="basic-info">
                  <Stat :label="$t('URL')"><PageLink :url="item.page.refPage" external>{{item.page.refPage}}</PageLink></Stat>
                </div>
              </div>
              <div v-else-if="item.page.pageType === 'cart'">
                <hr class="hr-line">
                <div class="basic-info">
                  <Stats>
                    <Stat :label="$t('SessionViewer.Gross Total')" type="currency" :currency="item.page.currencyIso || currency" :value="item.page.subTotal"/>
                    <Stat :label="$t('Discount')" type="currency" :currency="item.page.currencyIso || currency" :value="item.page.subTotal - item.page.subTotalLessDiscount"/>
                    <Stat :label="$t('SessionViewer.No of Products')" :value="item.page.itemCount"/>
                    <Stat :label="$t('SessionViewer.Item Quantity')" :value="item.page.itemQuantity"/> 
                  </Stats>
                </div>
              </div>
              <div v-else-if="(item.page.pageType === 'checkout' || item.page.pageType === 'orderReset') && item.order">
                <hr class="hr-line">
                <div class="basic-info">
                  <Stats>
                    <Stat :label="$t('SessionViewer.Order Number')" :value="item.order.orderNumber"/>
                    <Stat :label="$t('SessionViewer.Order Total')" type="currency" :currency="item.order.currency || currency" :value="item.order.orderTotal"/>
                    <Stat :label="$t('Discount')" type="currency" :currency="item.order.currency || currency" :value="item.order.discountAmount"/>
                    <Stat :label="$t('Discount Code')" :value="item.order.discountCode"/>
                  </Stats>
                </div>
              </div>
              <div v-else-if="(item.page.pageType === 'checkout' || item.page.pageType === 'orderReset') && item.page.refPage">
                <hr class="hr-line">
                <div class="basic-info">
                  <Stat :label="$t('URL')"><PageLink :url="item.page.refPage" external>{{item.page.refPage}}</PageLink></Stat>
                </div>
              </div>
              <!-- <pre>{{item.page}}</pre> -->
            </div>
          </div> 
          <div v-if="item.segments" :class="`sub-page-container ${index % 2 === 0 ? `sub-page-left` : `sub-page-right`}`"> 
            <div class="sub-page-card">
              <div class="sub-page-title">
                {{$t('SessionViewer.fellIntoSegments')}}
              </div>
              <hr class="hr-line"/>
              <div class="basic-info">
                <List tight>
                  <ListItem class="segment-list-item" v-for="(seg, j) in item.segments" :key="j" @mouseover="setExtraInfo(item.page.index, 'segment', seg)" @mouseleave="extraInfo = null">
                    {{seg.name || `${$t('SessionViewer.nameNotFound')} (${$t('ID')}: ${seg.id})`}}
                  </ListItem>
                </List>
              </div>
            </div>
          </div>
          <div v-if="item.widgets" :class="`sub-page-container ${index % 2 === 0 ? `sub-page-left` : `sub-page-right`}`"> 
            <div class="sub-page-card">
              <div class="sub-page-title">
                {{$t('SessionViewer.widgetsShown')}}
              </div>
              <hr class="hr-line"/>
              <div class="basic-info">
                <List tight>
                  <ListItem class="widget-list-item" v-for="(widgetObject, j) in item.widgets" :key="j" @mouseover="setExtraInfo(item.page.index, 'widget', widgetObject.widget)" @mouseleave="extraInfo = null">
                    {{widgetObject.widget.name}}
                    <List v-if="widgetObject.subTypes && widgetObject.subTypes.length">
                      <ListItem v-for="(st, k) in widgetObject.subTypes" :key="k">
                        <Caption>{{widgetEventTypeMap[st] || $t('Other') }}</Caption>
                      </ListItem>
                    </List>
                  </ListItem>
                </List>
              </div>
            </div>
          </div>
          <div v-if="item.cart" :class="`sub-page-container ${index % 2 === 0 ? `sub-page-left` : `sub-page-right`}`"> 
            <div class="sub-page-card">
              <div class="sub-page-title">
                {{$t('SessionViewer.cartChanged')}}
              </div>
              <hr class="hr-line"/>
              <div class="cart-content" style="padding: 0px;">
                <div class="cart-item">
                  <div class="cart-item-title">{{$t('SessionViewer.itemsInCart')}}</div>
                  <div v-if="item.cart.diff" class="cart-transition">{{item.cart.prevQuantity}} <Icon icon="fas fa-long-arrow-alt-right"/> {{item.cart.currQuantity}}</div>
                  <div v-if="item.cart.diff > 0" class="cart-item-change-positive">
                    + {{item.cart.diff}}
                  </div>
                  <div v-else-if="item.cart.diff < 0" class="cart-item-change-negative">
                    - {{-item.cart.diff}}
                  </div>
                  <template v-else>
                    <div class="cart-transition">{{item.page.itemQuantity}}</div>
                    <div class="cart-item-change-neutral">
                      + 0
                    </div>
                  </template>
                </div>
                <div class="cart-value">
                  <div class="cart-value-title">{{$t('SessionViewer.cartGrossValue')}}</div>
                  <div v-if="(item.cart.prevTotalValue < item.cart.currTotalValue) || (item.cart.prevTotalValue > item.cart.currTotalValue)" class="cart-transition">
                    <Formatter style="font-size: 14px;" type="currency" :currency="currency" :value="item.cart.prevTotalValue"/>  
                    {{' '}}<Icon style="font-size: 14px;" icon="fas fa-long-arrow-alt-right"/>{{' '}}
                    <Formatter style="font-size: 14px;" type="currency" :currency="currency" :value="item.cart.currTotalValue"/>
                  </div>
                  <div v-else class="cart-transition">
                    <Formatter style="font-size: 14px;" type="currency" :currency="currency" :value="item.page.subTotal"/>
                  </div>
                  <div v-if="item.cart.prevTotalValue < item.cart.currTotalValue" class="cart-value-change-positive">
                    + <Formatter style="font-size: 16px;" type="currency" :currency="currency" :value="item.cart.currTotalValue - item.cart.prevTotalValue"/>
                  </div>
                  <div v-else-if="item.cart.prevTotalValue > item.cart.currTotalValue" class="cart-value-change-negative">
                    - <Formatter style="font-size: 16px;" type="currency" :currency="currency" :value="-(item.cart.currTotalValue - item.cart.prevTotalValue)"/>
                  </div>
                  <div v-else class="cart-value-change-neutral">
                    + <Formatter style="font-size: 16px;" type="currency" :currency="currency" :value="0"/>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="item.coupon" :class="`sub-page-container ${index % 2 === 0 ? `sub-page-left` : `sub-page-right`}`"> 
            <div class="sub-page-card">
              <div class="sub-page-title">
                {{$t('SessionViewer.couponCodeChange')}}
              </div>
              <hr class="hr-line"/>
              <div v-if="item.coupon.removed" class="basic-info">
                {{$t('SessionViewer.timeline.Removed coupon code')}} <b>&lsquo;{{item.coupon.couponCode}}&rsquo;</b>
              </div>
              <div v-else class="basic-info">
                {{$t('SessionViewer.timeline.Applied coupon code')}} <b>&lsquo;{{item.coupon.couponCode}}&rsquo;</b>
              </div>
            </div>
          </div>
          <div v-if="item.offer" :class="`sub-page-container ${index % 2 === 0 ? `sub-page-left` : `sub-page-right`}`"> 
            <div class="sub-page-card">
              <div class="sub-page-title">
                <template v-if="item.offer.svStatus === 'presentedCollected'">
                  {{$t('SessionViewer.presentedCollectedOffer')}}
                </template>
                <template v-else-if="item.offer.svStatus === 'presented'">
                  {{$t('SessionViewer.presentedOffer')}}
                </template>
                <template v-else-if="item.offer.svStatus === 'collected'">
                  {{$t('SessionViewer.collectedOffer')}}
                </template>
              </div>
              <hr class="hr-line"/>
              <div class="basic-info offer-label" @mouseover="setExtraInfo(item.page.index, 'offer', item.offer)" @mouseleave="extraInfo = null">
                <b>{{item.offer.label}}</b>
                <Caption>{{item.offer.description}}</Caption>
              </div>
            </div>
          </div>
          <div v-if="item.notification" :class="`sub-page-container ${index % 2 === 0 ? `sub-page-left` : `sub-page-right`}`"> 
            <div class="sub-page-card">
              <div class="sub-page-title">
                {{$t('Notification')}}
              </div>
              <hr class="hr-line"/>
              <div class="basic-info">
                {{$t('SessionViewer.timeline.notificationSummary', [`${item.notification.type}`, `${item.notification.name}`, `${item.notification.action === 'default' ? $t('SessionViewer.timeline.using default action') : item.notification.action}`])}}
              </div>
            </div>
          </div>
          <div v-if="item.ab" :class="`sub-page-container ${index % 2 === 0 ? `sub-page-left` : `sub-page-right`}`"> 
            <div class="sub-page-card">
              <div class="sub-page-title">
                {{$t('A/B Testing')}}
              </div>
              <hr class="hr-line"/>
              <div v-if="item.ab.status === 'control'" class="basic-info">
                {{$t('SessionViewer.timeline.controlMessage')}}
              </div>  
              <div v-else-if="item.ab.status === 'targeted'" class="basic-info">
                {{$t('SessionViewer.timeline.targetedMessage')}}
              </div>  
            </div>
          </div>
        </div>
      </div>
      <div class="timeline-date">
        <Formatter type="dateTimeLong" :value="reverse ? firstPage.timestamp : lastPage.timestamp"/>
      </div>
    </div>
    <div v-else-if="loading || !sessionFlow" class="timeline-wrapper">
      <div class="timeline">
        <div v-for="(item, index) in [1, 2, 3, 4, 5]" :key="index" class="timeline-item">
          <div :class="`page-container ${index % 2 === 0 ? `page-left` : `page-right`}`"> 
            <div class="page-card">
              <SkeletonBodyText style="padding: 15px;"/>
            </div>
          </div> 
        </div>
      </div>
    </div>
    <!-- <pre>{{session}}</pre> -->
    <Sheet 
      :title="$t('Conversion Details')"
      :width="700"
      :visible="!!selectedConv"
      @close="selectedConv = null"
      padded
    >
      <Card
        v-if="selectedConv"
        :title="$t('Conversion')"
        :actions="[{
          label: orderAsRaw ? $t('Formatted') : $t('Raw'),
          onAction: () => { orderAsRaw = !orderAsRaw }
        }]"
      >
        <template v-if="!orderAsRaw">
          <CardSection>
            <Stats>
              <Stat
                :label="$t('Date')"
                type="dateTimeLong"
                :value="selectedConv.date"
              ></Stat>
              <Stat
                :label="$t('Domain')"
              >{{selectedConv.domain}}</Stat>
              <Stat
                :label="$t('Fanplayr Assisted')"
              >{{selectedConv.isFP}}</Stat>
              <Stat
                :label="$t('Fanplayr Charge')"
                type="currency"
                :currency="theUser.accountCurrency"
                :value="selectedConv.fpCharge"
              ></Stat>
            </Stats>
            <!-- <pre>{{selectedConv}}</pre> -->
          </CardSection>
          <CardSection :title="$t('Conversion rules')">
            <List>
              <ListItem
                v-for="(text, index) in selectedConv.textLines"
                :key="index"
              >{{text}}</ListItem>
            </List>
          </CardSection>
        </template>
        <template v-else>
          <pre class="raw-data">{{ selectedConv }}</pre>
        </template>
      </Card>
    </Sheet>
    <Sheet
      :title="!!pageOrder ? $t('SessionViewer.Order Details') : $t('SessionViewer.Page Details')"
      :width="700"
      :visible="!!page"
      @close="page = null"
      padded
    >
      <Card
        v-if="page && !pageOrder"
        :title="$t('SessionViewer.Page Overview')"
        :actions="[{
          label: pageAsRaw ? $t('Formatted') : $t('Raw'),
          onAction: () => { pageAsRaw = !pageAsRaw }
        }]"
      >
        <template v-if="!pageAsRaw">
          <CardSection>
            <Stats>
              <Stat :label="$t('URL')">
                <a v-if="page.refPage" :href="page.refPage" target="_blank">{{page.refPage}}</a> 
                <template v-else>—</template>
              </Stat>
              <Stat :label="$t('Type')" :value="formatPageType(page.pageType)"/>
              <Stat :label="$t('SessionViewer.Category')"><div class="stat-text-wrap">{{formatIdAndName(page.category, page.categoryName) || '—'}}</div></Stat>
              <Stat :label="$t('SessionViewer.Visit Time')" type="dateTimeLong" :value="convertHxDate(page.visitTime)"/>
              <Stat :label="$t('SessionViewer.Customer Email')" :value="page.customerEmail"/>
              <Stat :label="$t('SessionViewer.Customer ID')" :value="page.customerId"/>
            </Stats>
          </CardSection>
        </template>
        <template v-else>
          <pre class="raw-data">{{ page }}</pre>
        </template>
      </Card>
      <Card
        v-if="page && pageOrder"
        :title="$t('Order')"
        :actions="[{
          label: orderAsRaw ? $t('Formatted') : $t('Raw'),
          onAction: () => { orderAsRaw = !orderAsRaw }
        }]"
      >
        <template v-if="!orderAsRaw">
          <CardSection>
            <Stats>
              <Stat 
                :label="$t('SessionViewer.Order Number')"
                :value="pageOrder.orderNumber"
              />
              <Stat 
                :label="$t('Discount Code')"
                :value="pageOrder.discountCode"
              />
              <Stat
                :label="$t('SessionViewer.Customer Name')"
                :value="pageOrder.firstName ? pageOrder.firstName + ' ' + pageOrder.lastName : null"
              />
              <Stat
                :label="$t('SessionViewer.Order Date')"
                type="dateTimeLong"
                :value="pageOrder.orderDate"
              />
              <Stat
                :label="$t('SessionViewer.Customer Email')"
                :value="pageOrder.customerEmail"
              />
              <Stat
                :label="$t('SessionViewer.Order Email')"
                :value="pageOrder.orderEmail"
              />
              <Stat :label="$t('SessionViewer.Order Total')">   
                <Formatter type="currency" :currency="pageOrder.currency || currency" :value="pageOrder.orderTotalLocalCurrency"/>
                <div v-if="pageOrder.currency !== currency" style="opacity: 0.8">(<Formatter type="currency" :currency="currency" :value="pageOrder.orderTotal"/>)</div>
              </Stat>
              <Stat :label="$t('SessionViewer.Discount Amount')">
                <Formatter type="currency" :currency="pageOrder.currency || currency" :value="pageOrder.discountAmountLocalCurrency"/>
                <div v-if="pageOrder.currency !== currency" style="opacity: 0.8">(<Formatter type="currency" :currency="currency" :value="pageOrder.discountAmount"/>)</div>
              </Stat>
              <Stat :label="$t('SessionViewer.Shipping')">
                <Formatter type="currency" :currency="pageOrder.currency || currency" :value="pageOrder.shippingLocalCurrency"/>
                <div v-if="pageOrder.currency !== currency" style="opacity: 0.8">(<Formatter type="currency" :currency="currency" :value="pageOrder.shipping"/>)</div>
              </Stat>
              <Stat :label="$t('SessionViewer.Tax')">
                <Formatter type="currency" :currency="pageOrder.currency || currency" :value="pageOrder.taxLocalCurrency"/>
                <div v-if="pageOrder.currency !== currency" style="opacity: 0.8">(<Formatter type="currency" :currency="currency" :value="pageOrder.tax"/>)</div>
              </Stat>
            </Stats>
          </CardSection>
        </template>
        <template v-else>
          <pre class="raw-data">{{ pageOrder }}</pre>
        </template>
      </Card>
      <Card
        v-if="page && page.pageType === 'prod' && productExists"
        :title="$t('SessionViewer.viewedProduct')"
        :actions="[{
          label: productAsRaw ? $t('Formatted') : $t('Raw'),
          onAction: () => { productAsRaw = !productAsRaw }
        }]"
      >
        <template v-if="!productAsRaw">
          <CardSection>
            <div>
              <div v-if="page.productImage" style="float: left; margin-right: 15px;">
                <img :src="page.productImage" :alt="$t('SessionViewer.Product Image')" width="100"/>
              </div>
              <Stats>
                <Stat :label="$t('ID')" :value="page.product"/>
                <Stat :label="$t('Name')"><div class="stat-text-wrap">{{page.productName}}</div></Stat>
                <Stat :label="$t('Price')" type="currency" :currency="page.currencyIso || currency" :value="page.productPrice"/>
                <Stat :label="$t('Currency')" :value="page.currencyIso || currency"/>
                <Stat :label="$t('SessionViewer.SKU')" :value="page.productSku"/>
                <Stat :label="$t('URL')">{{page.productUrl}}</Stat>
              </Stats>
            </div>
          </CardSection>
        </template>
        <template v-else>
          <pre class="raw-data">{{ {
            productImage: page.productImage,
            product: page.product,
            productName: page.productName,
            productPrice: page.productPrice,
            currencyIso: page.currencyIso,
            productSku: page.productSku,
            productUrl: page.productUrl,
          } }}</pre>
        </template>
      </Card>
      <Card
        v-if="page && !pageOrder && page.itemCount"
        :title="$t('SessionViewer.Cart Overview')"
        :actions="[{
          label: cartOverviewAsRaw ? $t('Formatted') : $t('Raw'),
          onAction: () => { cartOverviewAsRaw = !cartOverviewAsRaw }
        }]"
      >
        <template v-if="!cartOverviewAsRaw">
          <CardSection>
            <Stats>
              <Stat :label="$t('SessionViewer.Gross Total')" type="currency" :currency="page.currencyIso || currency" :value="page.subTotal"/>
              <Stat :label="$t('Discount')" type="currency" :currency="page.currencyIso || currency" :value="page.subTotal - page.subTotalLessDiscount"/>
              <Stat :label="$t('SessionViewer.No of Products')" :value="page.itemCount"/>
              <Stat :label="$t('SessionViewer.Item Quantity')" :value="page.itemQuantity"/> 
              <Stat :label="$t('SessionViewer.Coupon Code')" :value="page.couponCode"/>
              <Stat :label="$t('Currency')" :value="page.currencyIso || currency"/>
            </Stats>
          </CardSection>
        </template>
        <template v-else>
          <pre class="raw-data">{{ {
            subTotal: page.subTotal,
            subTotalLessDiscount: page.subTotalLessDiscount,
            itemCount: page.itemCount,
            itemQuantity: page.itemQuantity,
            couponCode: page.couponCode,
            currencyIso: page.currencyIso,
           } }}</pre>
        </template>
      </Card>
      <Card
        v-if="page && getCartDetails(page, pageOrder) && getCartDetails(page, pageOrder).length"
        :title="`${$t('SessionViewer.Cart Details')} (${getCartAction})`"
        :actions="[{
          label: cartDetailsAsRaw ? $t('Formatted') : $t('Raw'),
          onAction: () => { cartDetailsAsRaw = !cartDetailsAsRaw }
        }]"
      >
        <template v-if="!cartDetailsAsRaw">
          <CardSection v-for="(prod, index) of getCartDetails(page, pageOrder)" :key="index" class="CartProduct">
            <div v-if="prod.image" style="float: left; margin-right: 15px;">
              <img :src="prod.image" :alt="$t('SessionViewer.Product Image')" width="100"/>
            </div>
            <div v-else style="float: left; width: 115px; height: 100px;"></div>
            <Stats>
              <Stat :label="$t('ID')">{{prod.id}}</Stat>
              <Stat :label="$t('Name')"><div class="stat-text-wrap">{{ prod.name }}</div></Stat>
              <Stat :label="$t('Price')" type="currency" :currency="page.currencyIso || currency" :value="prod.price"/>
              <Stat v-if="prod.url" :label="$t('URL')"><a :href="prod.url" target="_blank">{{prod.url}}</a></Stat>
              <Stat :label="$t('Quantity')">{{prod.qty}}</Stat>
              <Stat v-if="prod.sku" :label="$t('SessionViewer.SKU')" :value="prod.sku"/>
              <Stat v-if="prod.catId" :label="$t('Category ID')">{{prod.catId}}</Stat>
              <Stat v-if="prod.catName" :label="$t('Category Name')">{{prod.catName}}</Stat>
              <template v-if="prod.categories">
                <template v-for="key of Object.keys(prod.categories)">
                  <template v-if="key !== prod.catId">
                    <Stat :key="`${key}0`" :label="$t('Category ID')">{{prod.categories[key].id}}</Stat>
                    <Stat :key="`${key}1`" :label="$t('Category Name')">{{prod.categories[key].name}}</Stat>
                  </template>
                </template>
              </template>
            </Stats>
          </CardSection>
        </template>
        <template v-else>
          <pre class="raw-data">{{ getCartDetails(page, pageOrder) }}</pre>
        </template>
      </Card>
      <Card
        v-if="page"
        :title="`${$t('Custom Data')} (${$t(page.repeatCustomData ? 'SessionViewer.Repeat' : 'SessionViewer.Override')})`"
        :actions="[{
          label: pageCustomDataAsRaw ? $t('Formatted') : $t('Raw'),
          onAction: () => { pageCustomDataAsRaw = !pageCustomDataAsRaw }
        }]"
      >
        <template v-if="!pageCustomDataAsRaw">          
          <CardSection v-if="page.customValues && customValuesArray(page).length">
            <!-- <pre style="width: 100%; max-width: 590px; overflow-x: scroll;">{{ page.customValues.h ? page.customValues.h : page.customValues }}</pre> -->
            <List tight :emptyText="$t('No Custom Data')">
              <ListItem v-for="(data, index) in customValuesArray(page)" :key="index">{{data}}</ListItem>
            </List>
            <!-- {{page.customValues}} -->
          </CardSection>
          <CardSection v-else>
            <List tight :emptyText="$t('No Custom Data')"/>
          </CardSection>
        </template>
        <template v-else>
          <pre class="raw-data">{{ page.customValues }}</pre>
        </template>
      </Card>

    </Sheet>
    <Sheet
      :title="$t('Details')"
      :width="700"
      :visible="viewSessionDetails"
      @close="viewSessionDetails = false"
      padded
    >
      <template v-if="sessionFlow">
        <Card class="card" :title="$t('SessionViewer.Session Summary')">
          <CardSection>
            <Stats>
              <Stat
                :label="$t('SessionViewer.Session Key')"
                :value="session.sessionKey"
              />
              <Stat
                :label="$t('SessionViewer.Is Active')"
                :value="session.isActive ? $t('Yes') : $t('No')"
              />
              <Stat
                :label="$t('SessionViewer.Created')"
                type="dateLong"
                :value="convertHxDate(session.startTime)"
              />
              <Stat
                :label="$t('SessionViewer.Last Updated')"
                type="dateLong"
                :value="convertHxDate(session.lastAccessedTime)"
              />
              <Stat
                :label="$t('SessionViewer.Campaign Key')"
                :value="session.campaignKey"
              />
              <Stat
                :label="$t('SessionViewer.Site Details')"
                :value="`${$t('SessionViewer.siteId')}: ${session.userSite.siteId}`"
              />
              <Stat
                :label="$t('SessionViewer.A/B Group')"
                :value="format_ab(session.omittedByAbTest)"
              />
            </Stats>
          </CardSection>
        </Card>
        <Card class="card" :title="$t('SessionViewer.Origin')">
          <CardSection>
            <Stats>
              <Stat
                :label="$t('SessionViewer.Referrer')"
              >
                <a :href="session.referrer" target="_blank">{{session.referrer}}</a>
              </Stat>
              <Stat
                :label="$t('SessionViewer.Search Terms')"
                :value="session.searchTerm"
              />
              <Stat
                :label="$t('Source')"
                :value="session.source"
              />
              <Stat
                :label="$t('SessionViewer.Source Type')"
                :value="session.sourceType"
              />
              <Stat
                :label="$t('SessionViewer.New User?')"
                :value="session.isNewUser ? $t('Yes') : $t('No')"
              />
              <Stat
                :label="$t('SessionViewer.Converted User?')"
                :value="session.isConvertedUser ? $t('Yes') : $t('No')"
              />
              <Stat
                :label="$t('Email')"
                :value="session.email"
              />
            </Stats>
          </CardSection>
        </Card>
        <Card class="card" :title="$t('User')"
          :actions="[{
            label: $t('Details'),
            onAction: () => { $emit('show-user-timeline', true) }
          }]"
        >
          <CardSection>
            <Stats>
              <Stat
                :label="$t('SessionViewer.User Key')"
                :value="`${session.userKey} ${session.userSite.userId ? `(${session.userSite.userId})` : ''}`"
              />
              <Stat 
                :label="$t('SessionViewer.Location')"
                :value="`${session.city ? session.city + ', ' : ' '}${session.region ? session.region + ', ' : ' '}${session.country}`"
              />
              <Stat
                :label="$t('Postal Code')"
                :value="session.postalCode"
              />
              <Stat
                :label="$t('Timezone')"
                :value="formatTimezone(session.timeZone)"
              />
              <Stat
                :label="$t('SessionViewer.User Agent')"
              >{{ session.userAgent }}</Stat>
              <Stat
                :label="$t('IP Address')"
                :value="session.ipAddress"
              />
              <Stat
                :label="$t('SessionViewer.Customer ID')"
                :value="session.lookupMap ? session.lookupMap.CUSTOMER_ID : null"
              />
              <Stat
                v-if="session.userSite && session.userSite.ignoreUserUntil"
                :label="$t('SessionViewer.Ignore Until')"
              >
                <Formatter type="dateTimeShort" :value="convertHxDate(session.userSite.ignoreUserUntil)"/>
                <!-- <div>
                  <Badge v-if="isIgnoring">{{$t('SessionViewer.Currently Being Ignored')}}</Badge>
                  <Badge v-else>{{$t('SessionViewer.Ignore Period Ended')}}</Badge>
                </div> -->
              </Stat>
              <Stat 
                v-else
                :label="$t('SessionViewer.Ignore Until')"
                :value="$t('N/A')"
              />
            </Stats>
          </CardSection>
        </Card>
        <Card 
          v-if="session.userSite && session.userSite.customData"
          class="card" 
          :title="$t('User Custom Data')"
          :actions="[{
            label: userCustomDataAsRaw ? $t('Formatted') : $t('Raw'),
            onAction: () => { userCustomDataAsRaw = !userCustomDataAsRaw }
          }]"
        >
          <template v-if="!userCustomDataAsRaw">          
            <CardSection>
              <List tight :emptyText="$t('No Custom Data')">
                <ListItem v-for="(data, index) in userCustomValuesArray(session.userSite.customData)" :key="index">{{data}}</ListItem>
              </List>
            </CardSection>
          </template>
          <template v-else>
            <pre class="user-raw-custom-data">{{ session.userSite && session.userSite.customData }}</pre>
          </template>
        </Card>
        <Card class="card" :title="$t('Orders')">
          <template v-if="orders && orders.length">
            <CardSection
              v-for="order of orders" :key="order.orderNumber"
              :title="$t('SessionViewer.Order Number')"
              :actions="[{
                label: $t('Details'),
                onAction: () => { showOrderOrphan(order) }
              }]"
            >
              <Stats>
                <Stat
                  :value="`${order.orderNumber}`"
                />
              </Stats>
            </CardSection>
          </template>
          <CardSection v-else>
            <List tight :emptyText="$t('No Orders')"/>
          </CardSection>
        </Card>
        <Card class="card" :title="$t('Conversions')">
          <template v-if="conversions && conversions.length">
            <CardSection
              v-for="conversion of conversions" :key="conversion.id"
              :title="$t('Conversion rules')"
              :actions="[{
                label: $t('Details'),
                onAction: () => { showConversion(conversion) }
              }]"
            >
              <List>
                <ListItem
                  v-for="(text, index) in getConversionTextLines(conversion)"
                  :key="index"
                >{{text}}</ListItem>
              </List>
            </CardSection>
          </template>
          <CardSection v-else>
            <List tight :emptyText="$t('No Conversions')"/>
          </CardSection>
        </Card>
        <SessionViewerRFM 
          class="card" 
          :session="sessionFlow.session"
          :currency="domainCurrency || accountCurrency"
        />
        <SessionViewerDeviceStats 
          class="card" 
          :deviceStats="sessionFlow.session && sessionFlow.session.userSite && sessionFlow.session.userSite.deviceStats" 
          :userAgent="sessionFlow.session && sessionFlow.session.userAgent"
          :currency="accountCurrency"
        />
      </template>
    </Sheet>
  </div>
</template>

<script>

import UI from 'fanplayr-ui'
import _ from 'lodash'
import SessionViewerRFM from './SessionViewerRFM'
import SessionViewerDeviceStats from './SessionViewerDeviceStats'

export default {
  name: 'SessionViewerTimeline',
  props: {
    sessionFlow: {
      type: Object,
      required: true
    },
    currency: {
      type: String,
      required: true
    },
    domainCurrency: {
      type: String,
      required: true
    },
    accountCurrency: {
      type: String,
      required: true
    },
    history: {
      type: Object,
      required: true
    },
    reverse: {
      type: Boolean,
      required: true
    },
    conversions: {
      type: Array,
      required: true
    },
    fromSession: {
      type: Object
    },
    loading: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      page: null,
      pageTypeIconMap: {
        'home': 'fas fa-home',
        'prod': 'fas fa-tshirt',
        'cat' : 'fas fa-images',
        'srch': 'fas fa-search',
        'cart': 'fas fa-shopping-cart',
        'blog': 'fas fa-blog',
        'page': 'fas fa-file',
        'checkout': 'fas fa-dollar-sign',
        'orderReset': 'fas fa-money-check-alt'
      },
      cartDetailsAsRaw: false,
      pageCustomDataAsRaw: false,
      userCustomDataAsRaw: false,
      cartOverviewAsRaw: false,
      productAsRaw: false,
      orderAsRaw: false,
      pageAsRaw: false,
      selectedConv: null,
      viewSessionDetails: false,
      extraInfo: null,
      showAllMiniTimeline: false,
      filters: {},
      query: null,
      sortBy: {
        column: 'date',
        direction: 'asc'
      }
    }
  },
  methods: {
    goToSession(index) {
      if (this.sessionHistory) {
        // this.$router.push(`/sessionViewer/?sk=${this.sessionHistory[index].sk}`)
        this.$emit('update-session-key', this.sessionHistory[index].sk)
      }
    },
    goToSessionByKey(key) {
      this.$emit('update-session-key', key)
    },
    humanizeDuration(thisPageTimestamp, firstPageTimestamp) {
      return UI.format(window.moment.duration(window.moment(thisPageTimestamp).diff(window.moment(firstPageTimestamp))), 'durationMs')
    },
    getCartDetails(page, pageOrder) {
      if (page && page.cartDetails) {
        const data = JSON.parse(page.cartDetails)
        return data && data.length ? data : []
      } else if (pageOrder && pageOrder.cartDetails) {
        const data = JSON.parse(pageOrder.cartDetails)
        return data && data.length ? data : []
      }
      return []
    },
    formatIdAndName(id, name) {
      if ( name && id ) {
        return `${name} (${id})`;
      }
      if ( id ) {
        return `${this.$t('ID')}: ${id}`;
      }
      if ( name ) {
        return name;
      }
      return '';
    },
    getMinPurchase(offer) {
      if (offer.minPurchase >= 0) {
        return offer.minPurchase
      } else if (offer.minPurchaseText) {
        return offer.minPurchaseText
      } else {
        return this.$t('None')
      }
    },
    convertHxDate(time) {
      if (time && time.__t) {
        const date = window.moment(time.__t * 1000).format('YYYY-MM-DD HH:mm:ss')
        return window.moment(date)
                .utcOffset(this.session.timeZone)
                .tz(this.theUser.accountTimezone)
                .format('YYYY-MM-DD HH:mm:ss')
      }
      return ''
    },
    convertDate(date) {
      return date && window.moment.tz(date, 'GMT')
        .tz(this.theUser.accountTimezone)
        .format('YYYY-MM-DD HH:mm:ss')
    },
    formatTimezone(offset) {
      const hours = parseInt(offset) / -60
      return hours > 0 ? `${this.$t('UTC')}+${hours}:00` : `${this.$t('UTC')}-${Math.abs(hours)}:00`
    },
    format_ab(value) {
      if (value === 0) {
        return this.$t('SessionViewer.Targeted')
      }
      return value ? this.$t('SessionViewer.abControl', { pageNumber: `${this.session.abPageIndex + 1}` }) : this.$t('SessionViewer.Not Targeted')
    },
    formatStatus(stat) {
      return stat ? this.$t(_.capitalize(stat)) : ''
    },
    formatDate(stringDate) {
      return UI.format(stringDate, 'dateTimeShort')
    },
    getPageIcon(index) {
      if (this.sessionFlow.session.pages[index]) {
        const pageType = this.sessionFlow.session.pages[index].pageType
        return this.pageTypeIconMap[pageType]
      }
      return ''
    },
    formatPageType(pageType) {
      return this.pageTypeMap[pageType] || this.$t('Other')
    },
    showPageDetails(index) {
      this.page = this.sessionFlow.session.pages[index]
    },
    showConversion(conv) {
      const formattedConv = {
        ...conv,
        textLines: this.getConversionTextLines(conv)
      }
      this.selectedConv = formattedConv
    },
    getConversionText(type, value) {
      const fn = this.conversionTypes()[type]
      if (fn) {
        return fn(value)
      }
      // eslint-disable-next-line
      console.error(`Unknown conversion type "${type}"`)
      return this.$t(`conversionType`, { value: type })
    },
    getConversionTextLines(item) {
      return [1, 2, 3]
        .map(i => {
          const type = item[`action${i}Type`]
          const value = item[`action${i}Reference`]
          return type ? this.getConversionText(type, value) : null
        })
        .filter(d => !!d)
    },
    showOrderOrphan(order) {
      this.page = { quoteId: order.orderNumber }
      this.pageOrder = order
    },
    subItemExists(item) {
      return item.notification || item.cart || item.coupon || item.offer || item.ab
    },
    customValuesArray(page) {
      const rv = []
      let customValuesObject = page.customValues

      if (page.customValues && page.customValues.h) {
        customValuesObject = page.customValues.h
      }

      Object.keys(customValuesObject).forEach(k => {
        rv.push(`${k} : ${customValuesObject[k]}`)
      })
      return rv
    },
    userCustomValuesArray(customValues) {
      const rv = []
      Object.keys(customValues).forEach(k => {
        rv.push(`${k} : ${customValues[k]}`)
      })
      return rv
    },
    setExtraInfo(index, type, value) {
      // console.log(value)
      this.extraInfo = value ? {
        pageIndex: index,
        type,
        value
      } : null
    },
    conversionTypes() {
      let self = this
      return {
        ANY_ORDER() {
          return self.$t('Any order received')
        },
        FANPLAYR_ORDER() {
          return self.$t('Fanplayr order received')
        },
        NON_FANPLAYR_ORDER() {
          return self.$t('Non-Fanplayr order received')
        },
        FORM_SUBMIT(value) {
          return value
            ? self.$t(`formSubmitted`, { name: value })
            : self.$t('Any form submitted')
        },
        EMAIL_COLLECTION() {
          return self.$t('Email collected by any widget')
        },
        WIDGET_VIEW(value) {
          return value
            ? self.$t('widgetViewed', { value: value })
            : self.$t('Any widget viewed')
        },
        WIDGET_INTERACTION(value) {
          return value
            ? self.$t(`widgetButtonClicked`, { value: value })
            : self.$t('Any widget button clicked')
        },
        SAAS_TRIGGERED(value) {
          return self.$t(`segmentWithTagTriggered`, { name: value })
        },
        ANY_PAGE_URL_CONTAINS(value) {
          return self.$t(`visitedPageUrl`, { value: value })
        },
        SOURCE_WEB_PUSH() {
          return self.$t(`Session source was Web Push Notification`)
        }
      }
    },
    getMiniTimeline() {
      if (!this.sessionHistory || !this.sessionHistory.length) return []
      
      let lastYear = window.moment(this.sessionHistory[0].date).year()
      let timelineHistory = [{
        type: 'year',
        year: lastYear
      }]
      this.sessionHistory.forEach(h => {
        const momentDate = window.moment(h.date)
        let currYear = momentDate.year()
        if (currYear !== lastYear) {
          lastYear = currYear
          timelineHistory.push({
            type: 'year',
            year: lastYear
          })
        }
        timelineHistory.push({
          type: 'session',
          sk: h.sk,
          day: momentDate.date(),
          month: UI.format(momentDate.format('YYYY-MM-DD HH:mm:ss'), 'dateMonthShort')
        })
      })
      return timelineHistory.reverse()
    },
  },
  computed: {
    flow() {
      let flow = []
      if (this.sessionFlow && this.sessionFlow.flow) {
        flow = this.sessionFlow.flow.filter(item => {
          if (this.filters.hasEvent && this.filters.hasEvent.length) {
            for (const element of this.filters.hasEvent) {
              if (item[element]) {
                return true
              }
            }
            return false
          }
          if (this.filters.pageType && this.filters.pageType.length) {
            for (const pt of this.filters.pageType) {
              if (item.page.pageType === pt) {
                return true
              }
            }
            return false
          }
          return true
        })
        if (this.query) {
          flow = flow.filter(item => JSON.stringify(item).toLowerCase().indexOf(this.query.toLowerCase()) >= 0)
        }
      }
      if (this.reverse) {
        return [...flow].reverse()
      }
      return flow
    },
    pageTypeMap() {
      return {
        'home': this.$t('SessionViewer.timeline.Homepage'),
        'prod': this.$t('SessionViewer.timeline.Product'),
        'cat' : this.$t('SessionViewer.timeline.categoryMultipleProducts'),
        'srch': this.$t('SessionViewer.timeline.Search'),
        'cart': this.$t('SessionViewer.timeline.Shopping Cart'),
        'blog': this.$t('SessionViewer.timeline.Blog'),
        'page': this.$t('Other'),
        'checkout': this.$t('SessionViewer.timeline.Checkout'),
        'orderReset': this.$t('Order')
      }
    },
    widgetEventTypeMap() {
      return {
        'view': this.$t('View'),
        'minimizeAction': this.$t('creative.action.Minimize widget'),
        'maximizeAction': this.$t('creative.action.Maximize widget'),
        'closeWidgetAction': this.$t('creative.action.Close Widget'),
        'navigateUrlAction': this.$t('creative.action.Navigate to URL'),
        'refreshPageAction': this.$t('creative.action.Refresh Page'),
        'switchViewAction': this.$t('creative.action.Switch to view'),
        'previousViewAction': this.$t('creative.action.Switch to previous view'),
        'showViewAction': this.$t('creative.action.Show View'),
        'hdeViewAction': this.$t('creative.action.Hide View'),
        'collectOfferAction': this.$t('creative.action.Collect Offer'),
        'applyToCartAction': this.$t('creative.action.Apply offer to cart'),
        'applyToCartManualAction': this.$t('creative.action.Apply specific offer code to cart'),
        'invokeGlobalFunctionAction': this.$t('creative.action.Invoke global JavaScript function'),
        'submitFormDataAction': this.$t('creative.action.Submit form data in the current view'),
        'requestPushAction': this.$t('creative.action.Request push notification'),
        'afn': this.$t('SessionViewer.AFN')
      }
    },
    pageTypeLabelValues() {
      return Object.keys(this.pageTypeMap).map(k => {
        return {
          label: this.pageTypeMap[k],
          value: k
        }
      })
    },
    pageEventLabelValues() {
      return [
        {
          label: this.$t('Segments'),
          value: 'segments'
        },
        {
          label: this.$t('Widgets'),
          value: 'widgets'
        },
        {
          label: this.$t('SessionViewer.couponCodes'),
          value: 'coupon'
        },
        {
          label: this.$t('Offers'),
          value: 'offer'
        },
        {
          label: this.$t('SessionViewer.Cart'),
          value: 'cart'
        },
        {
          label: this.$t('Notification'),
          value: 'notification'
        },
        {
          label: this.$t('A/B Testing'),
          value: 'ab'
        }
      ]
    },
    productExists() {
      return this.page.product || this.page.productSku || this.page.productName || this.page.productUrl || this.page.productImage
    },
    segmentTypeMap() {
      return {
        'display-none': this.$t('Hide Offers'),
        'exclude': this.$t('Do Not Target'),
        'ignore-user': this.$t('Ignore User'),
        'exit-intent': this.$t('Exit Intent'),
        'include': this.$t('Standard')
      }
    },
    isIgnoring() {
      if (this.session.userSite && this.session.userSite.ignoreUserUntil) {
        const now = window.moment().utc().tz(this.theUser.accountTimezone)
        const endIgnoreDate = window.moment(this.convertHxDate(this.session.userSite.ignoreUserUntil))
        return endIgnoreDate.isAfter(now)
      }
      return false
    },
    getCartAction() {
      let cartAction = this.page.cartAction
      if (this.page.repeatCart) {
        cartAction = this.$t('SessionViewer.Repeat');
      }
      if (!cartAction) {
        cartAction = this.$t('SessionViewer.Override');
      }
      // cartAction = ucfirst(cartAction);
      return cartAction
    },
    orders() {
      return this.sessionFlow && this.sessionFlow.orders
    },
    pageOrder() {
      if (this.page && this.orders) {
        return this.orders.find(o => o.orderNumber === this.page.quoteId)
      }
      return null
    },
    session() {
      return this.sessionFlow && this.sessionFlow.session
    },
    firstPage() {
      return this.sessionFlow.flow && this.sessionFlow.flow.length && this.sessionFlow.flow[0].page
    },
    shortenedMiniTimeline() {
      const miniTimeline = this.getMiniTimeline()
      return this.showAllMiniTimeline ? miniTimeline : miniTimeline.slice(0, 15)
    },
    allowShowAll() {
      const fullMiniTimeline = this.getMiniTimeline()
      return !this.showAllMiniTimeline && this.shortenedMiniTimeline.length === 15 && fullMiniTimeline.length > 15
    },
    monthArray() {
      return [
        'JAN',
        'FEB',
        'MAR',
        'APR',
        'MAY',
        'JUN',
        'JUL',
        'AUG',
        'SEP',
        'OCT',
        'NOV',
        'DEC'
      ]
    },
    sessionHistory() {
      // console.log(this.history.userSite.visits)
      let history = this.history && this.history.userSite && this.history.userSite.visits ? this.history.userSite.visits.map(v => {
        return {
          sk: v.sk,
          date: this.convertDate(v.end)
        }
      }) : []

      if (this.session) {
        const currSession = history.find(h => h.sk === this.session.sessionKey)
        if (!currSession) {
          history.push({
            sk: this.session.sessionKey,
            date: this.convertHxDate(this.session.lastAccessedTime)
          })
        } else if (this.fromSession && !history.find(h => h.sk === this.fromSession.sessionKey)) {
          history.push({
            sk: this.fromSession.sessionKey,
            date: this.fromSession.date
          })
        }
      }

      if (this.sortBy) {
        history = _.orderBy(history, [this.sortBy.column], [this.sortBy.direction])
      }
      return history
    },
    currentSessionIndex() {
      return this.sessionHistory && this.sessionHistory.findIndex(sh => sh.sk === this.session.sessionKey)
    },
    hasNextSession() {
      if (this.sessionHistory && this.currentSessionIndex !== null) {
        const lastIndex = (this.sessionHistory.length - 1)
        return this.currentSessionIndex < lastIndex
      }
      return false
    },
    hasPreviousSession() {
      return this.currentSessionIndex !== null && this.currentSessionIndex > 0
    },
    lastPage() {
      return this.sessionFlow.flow 
        && this.sessionFlow.flow.length 
        && this.sessionFlow.flow[this.sessionFlow.flow.length - 1].page
    }
  },
  components: {
    SessionViewerRFM,
    SessionViewerDeviceStats
  }
}
</script>

<style lang="scss">
.raw-data {
  width: 100%;
  max-width: 590px;
  overflow-x: scroll;
  background: #eee;
  margin: 15px;
  padding: 15px;
}

.user-raw-custom-data {
  width: 100%;
  overflow-x: scroll;
  background: #eee;
  padding: 10px;
}

#SessionViewerTimeline {
  .ignore-warning-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
     .ignore-warning {
      display: inline-block;
      padding: 7px;
      background: #FF9800;
      color: #fff;
      box-shadow: 0 1px 2px rgba(0,0,0,0.1);
      border-radius: 1px;
      margin-bottom: 15px;
      i {
        display: inline-block;
        margin-right: 7px;
        margin-left: 3px;
      }
      div {
        display: inline-block;
      }
    }
  }
 

  .mini-user-timeline {
    position: relative;
    // left: 10px;
    // overflow-x: hidden;
    z-index: 1;
    float: left;
    width: 52px;
    
    .history-element {
      // border: 3px solid #363C4B;
      // border-radius: 2px;
      // padding: 4px;
      margin-top: 20px;
      margin-bottom: 20px;
      font-size: 14px;
      position: relative;

      .history-element-year {
        font-weight: bold;
      }
      .history-element-year::after {
        content: "";
        position: absolute;
        width: 20px;
        height: 2px;
        background-color: #363C4B;
        // border: 3px solid #363C4B;
        top: 7px;
        // border-radius: 50%;
        z-index: 1;
        right: -7px;
      }
      

      .history-element-date {
        // border: 1px solid red;
        width: 30px;
        text-align: center;
        font-weight: bold;
        .history-element-date-month {
          line-height: 14px;
          text-transform: uppercase;
        }
        .history-element-date-day {
          line-height: 14px;
        }
      }
      .history-element-date::after {
        content: "";
        position: absolute;
        width: 20px;
        height: 20px;
        background-color: #FFFFFF;
        border: 3px solid #363C4B;
        top: 5px;
        border-radius: 50%;
        z-index: 1;
        right: -7px;
      }
      &.current-session {
        color: #000000 !important;
        .history-element-date::after {
          background-color: #FF9800 !important;
        }
      }
      &.session {
        &:hover {
          cursor: pointer;
          color: #2A9DF4;
          .history-element-date::after {
            background-color: #2A9DF4;
          }
        }
      }
    }

    .mini-timeline-show-all {
      margin-left: -20px;
      width: 70px;
      cursor: pointer;
      font-weight: bold;
      text-align: center;
      border-radius: 4px;
      padding: 5px;
      &:hover {
        color: #2A9DF4;
      }
    }
  }
  .mini-user-timeline::after {
    content: '';
    position: absolute;
    width: 2px;
    background-color: #363C4B;
    top: 10px;
    bottom: 10px;
    left: 95%;
    margin-left: -1px;
    z-index: -1;
  }

  .timeline-wrapper {
    width: 92%;
    float: right;
  }

  /* The actual timeline (the vertical ruler) */
  .timeline {
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
  }
  
  .timeline > div:first-of-type .page-container::before {
    top: 30px !important;
  }
  .timeline > div:first-of-type .page-left::after {
    top: 28px !important;
  }

  .timeline-item {
    padding-top: 15px;
    padding-bottom: 15px;
    overflow: hidden;
    position: relative;
    width: 100%;
  }

  .filter {
    padding-bottom: 0px;
    border-bottom: none;
  }

  .active-session-buttons {
    padding-bottom: 10px;
  }
  .session-buttons {
    padding-bottom: 10px;
  }
  

  .title-card {
    margin-bottom: 20px;
    .title-button-label-short {
      display: none;
    }
    .arrow-right {
      margin-left: 5px;
    }
    .arrow-left {
      margin-right: 5px;
    }
  }

  /* The actual timeline (the vertical ruler) */
  .timeline::after {
    content: '';
    position: absolute;
    width: 4px;
    background-color: #FF9800;
    top: 5px;
    bottom: 5px;
    left: 50%;
    margin-left: -3px;
  }
  /* Container around content */
  .page-container {
    padding: 5px 40px 0px 40px;
    position: relative;
    background-color: inherit;
    width: 50%;
  }
  .sub-page-container {
    padding: 10px 40px 0px 40px;
    position: relative;
    background-color: inherit;
    width: 40%;
  }

  /* The circles on the timeline */
  .page-container::after {
    content: '';
    position: absolute;
    width: 24px;
    height: 24px;
    background-color: #363C4B;
    border: 3px solid #EEEEEE;
    top: 10px;
    border-radius: 50%;
    z-index: 1;
    right: -11px;
  }

  .sub-page-container::after {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    background-color: #363C4B;
    border: 3px solid #EEEEEE;
    top: 22px;
    border-radius: 50%;
    z-index: 1;
    right: -7px;
  }


  /* The actual content */
  .page-card {
    // padding: 10px 15px;
    background-color: #FFFFFF;
    position: relative;
    border-radius: 3px;
    // border: 2px solid #E6DFDF;
    box-shadow: 0 0 0 1px rgba(63,63,68,.05), 0 1px 3px 0 rgba(63,63,68,.15);
    transition: transform .2s;
    &:hover {
      transform: scale(1.02);
    }
    cursor: pointer;
    a {
      display: block;
      width: 100%;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }

  .sub-page-card {
    background-color: #FFFFFF;
    position: relative;
    border-radius: 3px;
    box-shadow: 0 0 0 1px rgba(63,63,68,.05), 0 1px 3px 0 rgba(63,63,68,.15);
    a {
      display: block;
      width: 100%;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }

  .sub-page-title {
    padding: 7px;
    font-size: 14px;
  }

  .cart-content {
    padding: 12px;
    display: inline-block;
    overflow-wrap: break-word;
    height: 100%;
    width: 100%;
    vertical-align: middle;
  }

  .cart-content::after {
    content: '';
    position: absolute;
    width: 2px;
    background-color: #E6DFDF;
    left: 50%;
    top: 32px;
    bottom: 0px;
    margin-left: -2px;
    // height: 100%;
  }


  /* Place the container to the left */
  .page-left {
    left: 0;
  }
  .sub-page-left {
    left: 10%;
  }

  /* Place the container to the right */
  .page-right, .sub-page-right {
    left: 50%;
  }

  /* Add arrows to the left container (pointing right) */
  .page-left::before {
    content: " ";
    height: 0;
    position: absolute;
    top: 12px;
    width: 0;
    z-index: 0;
    right: 30px;
    border: medium solid #E6DFDF;
    border-width: 10px 0 10px 10px;
    border-color: transparent transparent transparent #E6DFDF;
  }
  
  /* Add arrows to the left container (pointing right) */
  .sub-page-left::before {
    content: " ";
    height: 0;
    position: absolute;
    top: 21px;
    width: 0;
    z-index: 0;
    right: 34px;
    border: medium solid #E6DFDF;
    border-width: 10px 0 10px 10px;
    border-color: transparent transparent transparent #E6DFDF;
  }

  /* Add arrows to the right container (pointing left) */
  .page-right::before {
    content: " ";
    height: 0;
    position: absolute;
    top: 12px;
    width: 0;
    z-index: 0;
    left: 30px;
    border: medium solid #E6DFDF;
    border-width: 10px 10px 10px 0;
    border-color: transparent #E6DFDF transparent transparent;
  }

  /* Add arrows to the right container (pointing left) */
  .sub-page-right::before {
    content: " ";
    height: 0;
    position: absolute;
    top: 21px;
    width: 0;
    z-index: 0;
    left: 34px;
    border: medium solid #E6DFDF;
    border-width: 10px 10px 10px 0;
    border-color: transparent #E6DFDF transparent transparent;
  }

  /* Fix the circle for containers on the right side */
  .page-right::after {
    left: -13px;
  }

  .sub-page-right::after {
    left: -9px;
  }

  .timeline-date {
    // display: inline-block;
    position: relative;
    text-align: center;
    margin: 0 auto;
    width: 225px;
    background-color: #363C4B;
    z-index: 1;
    padding: 5px 15px;
    color: white;
    border-radius: 2px;
  }

  .extra-info-card-right, .extra-info-card-left {
    background: #FFFFFF;
    // height: 70%;
    width: 44%;
    position: absolute;
    top: 15%;
    border-radius: 3px;
    box-shadow: 0 0 0 1px rgba(63, 63, 68, 0.05), 0 1px 3px 0 rgba(63, 63, 68, 0.15);
  }

  .extra-info-card-left {
    left: 3%;
  }
  .extra-info-card-right {
    right: 3%;
  }


  .page-url {
    font-size: 12px;
    padding: 2px;
    display: none;
  }
  .page-type {
    // text-align: center;
    padding-top: 7px;
    padding-bottom: 7px;
    font-size: 16px;
    line-height: 20px;
    margin: 0px 0px 0px 50px;
  }
  .page-type-icon {
    margin-right: 10px;
  }
  .product-info {
    // display: inline;
    overflow: hidden;
    padding: 8px 15px 8px 15px;
    font-size: 16px;
    .product-info-name-value {
      padding-top: 10px;
      padding-bottom: 10px;
      .product-info-name {
        width: 75%;
        .product-category-name {
          font-size: 12px;
        }
        &.with-img {
          padding-left: 66px;
        }
      }
      .product-price {
        position: absolute;
        right: 15px;
        top: 46px;
        font-size: 16px;
        background: #363C4B;
        padding: 7px;
        border-radius: 10px;
        color: white;
        &.with-cat {
          top: 53px;
        }
      }
    }
    .product-info-image {
      float: left; 
      // margin-right: 15px; 
      border-radius: 1px;
      border: 2px solid #363C4B;
    }
  }


  .basic-info {
    padding: 10px;
  }
  .cart-item {
    width: 50%;
    text-align: center;
    float: left;
    padding: 8px;
  }
  .cart-item-title {
    font-size: 14px;
  }
  .cart-value-title {
    font-size: 14px;
  }
  .cart-value {
    width: 50%;
    text-align: center;
    float: right;
    padding: 8px;
    // border-left: 2px solid #E6DFDF;
  }
  .cart-transition {
    font-size: 14px;
    margin: 2px 0px 3px 0px;
  }
  .cart-item-change-positive {
    display: inline-block;
    padding: 4px 8px;
    // height: 30px;
    // width: 60px;
    background-color: #00b300;
    color: #FFFFFF;
    text-align: center;
    font-size: 16px;
    border-radius: 2px;
  }
  .cart-value-change-positive {
    display: inline-block;
    padding: 4px 8px;
    // height: 30px;
    // width: 100px;
    background-color: #00b300;
    color: #FFFFFF;
    text-align: center;
    font-size: 16px;
    border-radius: 2px;
  }
  .cart-value-change-negative {
    display: inline-block;
    padding: 4px 8px;
    // height: 30px;
    // width: 100px;
    background-color: #d64541;
    color: #FFFFFF;
    text-align: center;
    font-size: 16px;
    border-radius: 2px;
  }
  .cart-value-change-neutral {
    display: inline-block;
    padding: 4px 8px;
    // height: 30px;
    // width: 100px;
    background-color: #A9A9A9;
    color: #FFFFFF;
    text-align: center;
    font-size: 16px;
    border-radius: 2px;
  }
  .cart-item-change-negative {
    display: inline-block;
    padding: 4px 8px;
    // height: 30px;
    // width: 60px;
    background-color: #d64541;
    color: #FFFFFF;
    text-align: center;
    font-size: 16px;
    border-radius: 2px;
  }
  .cart-item-change-neutral {
    display: inline-block;
    padding: 4px 8px;
    // height: 30px;
    // width: 60px;
    background-color: #A9A9A9;
    color: #FFFFFF;
    text-align: center;
    font-size: 16px;
    border-radius: 2px;
  }
  .page-index {
    position: absolute;
    top: 0px;
    left: 0px;
    padding: 9px;
    background-color:#FF9800;
    color: #FFFFFF;
    border-radius: 2px 0px 0px 0px;
  }
  .page-time {
    position: absolute;
    right: 0px;
    top: 0px;
    padding: 9px;
    // background-color:#FF9800;
    // color: #FFFFFF;
    border-radius: 0px 2px 0px 0px;
  }
  .page-segments {
    margin-top: 5px;
  }
  .section-title {
    padding-top: 8px;
    padding-left: 8px;
  }

  .segment-list-item, .widget-list-item, .offer-label {
    &:hover {
      cursor: pointer;
      color: #2200CC;
    }
  }

  .hr-line {
    background-color: #E6DFDF;
    border: 1px solid #E6DFDF;
    margin: 0px;
  }

  // probably not right way to do this?


  @media screen and (max-width: 1000px) {
    .page-container {
      width: 100%;
      padding: 5px 0px;
      z-index: 1;
    }
    // .mini-user-timeline {
    //   display: none;
    // }
    .sub-page-container {
      width: 80%;
      padding: 5px 0px;
      z-index: 1;
    }
    .sub-page-right, .sub-page-left {
      left: 10%;
    }
    .page-right {
      left: 0;
    }
    .page-left::before, 
    .page-left::after, 
    .page-right::before, 
    .page-right::after,
    .sub-page-right::before,
    .sub-page-left::before,
    .sub-page-right::after,
    .sub-page-left::after {
      display: none;
    }

    .extra-info-card-left, .extra-info-card-right {
      display: none;
    }
    .segment-list-item, .widget-list-item, .offer-label {
      &:hover {
        cursor: inherit;
        color: inherit;
      }
    }
    .timeline-date {
      display: block;
      width: 225px;
      margin: 0 auto;
    }
    .timeline-wrapper {
      width: 88%;
      // float: none;
    }
  }

  @media screen and (max-width: 820px) {
    .title-card {
      .title-button-label-long {
        display: none;
      }
      .title-button-label-short {
        display: block;
      }
      .navigate-session-text {
        display: none;
      }
      .arrow-right {
        margin-left: 0px;
      }
      .arrow-left {
        margin-right: 0px;
      }
    }
  }

  @media screen and (max-width: 680px) {
    .mini-user-timeline {
      display: none;
    }
    .timeline-wrapper {
      width: 100%;
      float: none;
    }
  }

  @media screen and (max-width: 440px) {
    .filter {
      display: none;
    }
    .session-buttons {
      padding-bottom: 0px;
    }
    .reverse-order-button {
      display: none;
    }
  }
}
</style>